/* iter55g17 — Auto-save status pill.
 *
 * Sits in the editor header and reflects the live state of the shared
 * `useSaveState` machine. Four visuals:
 *
 *   idle    → subtle grey "Saved" (or nothing if no save yet)
 *   saving  → amber spinner + "Saving…"
 *   saved   → green check + "Saved" (fades to idle after HOLD_MS)
 *   error   → red exclaim + "Save failed — click to retry" tooltip
 *
 * The pill is deliberately quiet — 10.5px text, no heavy animation — so
 * it can live in a dense toolbar. On error it becomes clickable so the
 * writer can force a manual retry via the `onRetry` callback (if
 * provided).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 2
import { Loader2, Check, AlertTriangle, CloudOff } from 'lucide-react';

function formatWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = Date.now();
    const delta = Math.max(0, Math.round((now - d.getTime()) / 1000));
    if (delta < 60)  return 'just now';
    if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return null; }
}

export default function SaveStatusPill({
  state, lastSavedAt, lastError, onRetry, onSaveAs,
}) {
  const { t } = useTranslation();
  const shared = 'inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[13px] font-medium select-none transition-colors';

  if (state === 'saving') {
    return (
      <span
        className={`${shared} border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300`}
        data-testid="editor-save-status"
        data-save-state="saving"
        aria-live="polite"
      >
        <Loader2 className="h-3 w-3 animate-spin" /> {t('editor.saveStatus_saving')}
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span
        className={`${shared} border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300`}
        data-testid="editor-save-status"
        data-save-state="saved"
        aria-live="polite"
        title={lastSavedAt ? t('editor.saveStatus_lastSaved', { when: formatWhen(lastSavedAt) }) : t('editor.saveStatus_saved')}
      >
        <Check className="h-3 w-3" /> {t('editor.saveStatus_saved')}
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="inline-flex items-center gap-1" aria-live="assertive">
        <button
          type="button"
          onClick={onRetry}
          className={`${shared} cursor-pointer border-rose-500/40 bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 dark:text-rose-300`}
          data-testid="editor-save-status"
          data-save-state="error"
          title={lastError || t('editor.saveStatus_errorRetry')}
        >
          <AlertTriangle className="h-3 w-3" /> {t('editor.saveStatus_error')}
        </button>
        {onSaveAs && (
          <button
            type="button"
            onClick={onSaveAs}
            className="inline-flex items-center gap-0.5 rounded-sm border border-rose-500/30 bg-rose-500/5 px-1.5 py-0.5 text-[13px] font-medium text-rose-600 transition-colors hover:bg-rose-500/15 dark:text-rose-400"
            data-testid="editor-save-as-btn"
            title="Save your writing to a new chapter"
          >
            Save As…
          </button>
        )}
      </span>
    );
  }
  // idle
  if (!lastSavedAt) return null;
  return (
    <span
      className={`${shared} border-panel-border bg-transparent text-muted-foreground/70`}
      data-testid="editor-save-status"
      data-save-state="idle"
      title={t('editor.saveStatus_lastSaved', { when: formatWhen(lastSavedAt) })}
    >
      <CloudOff className="h-3 w-3 opacity-70" /> {t('editor.saveStatus_savedTime', { when: formatWhen(lastSavedAt) })}
    </span>
  );
}
